import { createSlice } from '@reduxjs/toolkit';
import { callApi } from 'src/utils/apiCall';
import { dispatch } from '../store';
import { updateError } from './user';
import { getPracticeId } from './clincheck';

const initialState = {
    wpNotifications: [],
    emailNotifications: [],
    notificationsJwt: '',
};

const slice = createSlice({
    name: 'liveNotifications',
    initialState,
    reducers: {
        resetWpNotifications(state) {
            Object.assign(state, initialState);
        },
        setPersistedWpNotifications(state, action) {
            const notification = action.payload;
            // Ensure notification has a unique ID for close functionality
            if (!notification.id) {
                notification.id = `wp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            }
            // Only add if notification with same ID doesn't already exist
            const exists = state.wpNotifications.some((n) => n.id === notification.id);
            if (!exists) {
                state.wpNotifications = [...state.wpNotifications, notification];
            }
        },
        setPersistedEmailNotifications(state, action) {
            const notification = action.payload;
            // Ensure notification has a unique ID for close functionality
            if (!notification.id) {
                notification.id = `email_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            }
            // Only add if notification with same ID doesn't already exist
            const exists = state.emailNotifications.some((n) => n.id === notification.id);
            if (!exists) {
                state.emailNotifications = [...state.emailNotifications, notification];
            }
        },
        // Live-update mutation for the same persisted-email-notifications
        // list. Used when the backend pushes a 'notification-updated'
        // PieSocket event — e.g. when a grouped click-tracking
        // notification's grouped_count gets bumped on every subsequent
        // click within the same day. We find the row by id and patch
        // description + grouped_count in place; no new toast, no array
        // append (which would create a phantom duplicate).
        updatePersistedEmailNotification(state, action) {
            const patch = action.payload;
            if (!patch || !patch.id) return;
            const idx = state.emailNotifications.findIndex((n) => n.id === patch.id);
            if (idx === -1) return;
            state.emailNotifications[idx] = {
                ...state.emailNotifications[idx],
                ...patch,
            };
        },
        setWpNotifications(state, action) {
            state.wpNotifications = action.payload;
        },
        setNotificationsJwt(state, action) {
            state.notificationsJwt = action.payload;
        },
        setEmailNotifications(state, action) {
            state.emailNotifications = action.payload;
        },
    },
});

export default slice.reducer;

export const { setWpNotifications, resetWpNotifications, setPersistedWpNotifications, setEmailNotifications, setPersistedEmailNotifications, updatePersistedEmailNotification } = slice.actions;

// ── Shared WhatsApp toast dedup set ──
// LiveNotificationHandler (_notifications channel) is the sole source of
// WhatsApp toasts.  This set is kept for safety — if the same WP message
// somehow arrives twice on the platform channel, the second one is skipped.
export const shownWpToastIds = new Set();

// ── General verb-based toast dedup set ──
// Prevents duplicate toasts when both frontend detection and backend PieSocket
// fire for the same event (e.g. follow-up table move).  Entries auto-expire
// via setTimeout so the Set stays small.
export const recentToastKeys = new Set();

// ── Notification ID dedup set ──
// Tracks notification IDs (any verb) that have already been shown as an
// in-app toast in the current session.  Auto-expires after 30s so the set
// stays small.  Used to prevent duplicates when:
//   • PieSocket delivers the same event twice (network retry / reconnect)
//   • Firebase foreground onMessage fires for the same event as PieSocket
export const shownNotifIds = new Set();

export function getNotificationsJwt(data) {
    return async () => {
        try {
            const practiceId = getPracticeId();
            const { userId } = data;
            const response = await callApi(`/api/v1/practices/${practiceId}/users/${userId}/notifications/generate_websocket_jwt/`, 'post');
            if (response.status === 200) {
                dispatch(slice.actions.setNotificationsJwt(response.data.auth));
            } else {
                dispatch(updateError({ success: false, message: JSON.stringify(response?.data) || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}